import { PageSize } from '@/constant';
import {
	GetCommonNoticeListParams,
	ICommonNotice,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated, mergeListItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '..';
import { formatCommonNoticeList } from './common-notice.helper';
import {
	ICommonNoticeDetailStore,
	ICreateNoticeRequest,
	IPayloadDeleteCommonNoticeRequest,
	IPayloadGetCommonNoticeDetailRequest,
	IPayloadGetCommonNoticeDetailSuccess,
	IPayloadGetCommonNoticeListRequest,
	IPayloadGetCommonNoticeListSuccess,
} from './common-notice.type';

const InitParams: GetCommonNoticeListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const initialState = {
	fetching: false,
	list: [] as ICommonNoticeDetailStore[],
	total: 0,
	params: InitParams,
	detail: {} as ICommonNoticeDetailStore,
};

const commonNoticeSlice = createSlice({
	initialState,
	name: 'commonNotice',
	reducers: {
		/**---- GET COMMON NOTICE LIST START ---- */
		getCommonNoticeListRequest: (
			state,
			action: PayloadAction<IPayloadGetCommonNoticeListRequest>
		) => {
			return {
				...state,
				fetching: true,
				params: action?.payload,
			};
		},
		getCommonNoticeListSuccess: (
			state,
			action: PayloadAction<IPayloadGetCommonNoticeListSuccess>
		) => {
			const { content, total } = action.payload;
			state.fetching = false;
			state.list = content;
			state.total = total;
		},
		getCommonNoticeListFailure: (state) => {
			state.fetching = false;
			state.total = 0;
			state.list = [];
		},
		/**---- GET COMMON NOTICE LIST END ---- */

		/**---- CREATE COMMON NOTICE START ---- */
		createCommonNoticeRequest: (
			state,
			action: PayloadAction<ICreateNoticeRequest>
		) => {
			state.fetching = true;
		},
		createCommonNoticeSuccess: (state) => {
			state.fetching = false;
		},
		createCommonNoticeFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE COMMON NOTICE END ---- */

		/**---- DELETE COMMON NOTICE START ---- */
		deleteCommonNoticeRequest: (
			state,
			action: PayloadAction<IPayloadDeleteCommonNoticeRequest>
		) => {
			state.fetching = true;
		},
		deleteCommonNoticeSuccess: (state) => {
			state.fetching = false;
		},
		deleteCommonNoticeFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE COMMON NOTICE START ---- */

		/**---- GET COMMON NOTICE DETAIL START ---- */
		getCommonNoticeDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetCommonNoticeDetailRequest>
		) => {
			state.fetching = true;
		},
		getCommonNoticeDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetCommonNoticeDetailSuccess>
		) => {
			const { detail } = action.payload;
			state.fetching = false;
			state.detail = detail;
		},
		getCommonNoticeDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET COMMON NOTICE DETAIL END ---- */

		/**---- UPDATE COMMON NOTICE START ---- */
		updateCommonNoticeSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<ICommonNotice>>
		) => {
			const { data } = action.payload;
			const list = [...state.list];
			state.list = formatCommonNoticeList(mergeListItemUpdated(list, data));
		},
		updateCommonNoticeDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<ICommonNotice>>
		) => {
			const { data } = action.payload;
			const detail = { ...state.detail };
			state.detail = mergeItemUpdated<ICommonNoticeDetailStore, ICommonNotice>(
				detail,
				data
			);
		},
		/**---- UPDATE COMMON NOTICE END ---- */
	},
});

export const CommonNoticeSelectors = {
	selectFetching: (state: RootState) => state.commonNotice.fetching,
	selectParams: (state: RootState) => state.commonNotice.params,
	selectList: (state: RootState) => state.commonNotice.list,
	selectTotal: (state: RootState) => state.commonNotice.total,
	selectDetail: (state: RootState) => state.commonNotice.detail,
};

export const CommonNoticeActions = commonNoticeSlice.actions;
export const CommonNoticeReducer = commonNoticeSlice.reducer;
